/**
 * Daily Record Page Route
 *
 * Protected route for viewing an individual daily pain record by date.
 * Displays pain locations, medications, notes, and tags for a specific date.
 * Supports editing the full day via check-in flow.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useDailyRecord } from "@/hooks";
import { DailyRecordView } from "@/components/daily-record/daily-record-view";

/**
 * Daily Record Page Component
 *
 * Fetches and displays a daily pain record for the date specified in the route params.
 */
function DailyRecordPage() {
  // The route tree will be regenerated and provide correct types
  const { date } = Route.useParams() as { date: string };
  const { record, isLoading, isError, error } = useDailyRecord(date);

  return (
    <DailyRecordView
      record={record}
      isLoading={isLoading}
      error={isError ? (error as Error) : null}
      date={date}
    />
  );
}

/**
 * Route configuration for /daily/:date
 *
 * Example URLs:
 * - /daily/2026-01-10
 * - /daily/2025-12-25
 */
export const Route = createFileRoute("/_authenticated/daily/$date")({
  component: DailyRecordPage,
});
